import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteField } from 'firebase/firestore';
import type { JobOverrides } from '@hrlabs/shared-types';
import { getRow, saveRow } from '../../lib/db';
import { useToast } from '../../components/Toast';
import { QuillEditor } from '../../components/QuillEditor';

export function JobEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [o, setO] = useState({
    engagement: '',
    area: '',
    workModel: '',
    seniority: '',
    city: '',
    salaryMin: '',
    salaryMax: '',
    currency: '',
    salaryPeriod: '',
    validThrough: '',
    blurbEn: '',
    blurbBg: '',
  });
  const [synced, setSynced] = useState<Record<string, string>>({});
  const [f, setF] = useState({
    title: '',
    status: 'published',
    content: '',
    workFromHome: false,
    salary: '' as string,
    currency: 'BGN',
  });

  useEffect(() => {
    if (!id) return;
    getRow('jobs', id).then((r) => {
      if (r)
        setF({
          title: String(r.title ?? ''),
          status: String(r.status ?? 'published'),
          content: String(r.content ?? ''),
          workFromHome: Boolean(r.workFromHome),
          salary: r.salary != null ? String(r.salary) : '',
          currency: String(r.currency ?? 'BGN'),
        });
      if (r) {
        const created = r.createdAt as { toDate?: () => Date } | undefined;
        setSynced({
          'Contract (Manatal)': String(r.contractDetails ?? '—'),
          'Location (Manatal)': String(r.locationDisplay ?? '—'),
          'Salary min (Manatal)': r.salaryMin != null ? String(r.salaryMin) : '—',
          'URL slug': String(r.slug ?? r.id),
          Posted: created?.toDate ? created.toDate().toISOString().slice(0, 10) : '—',
        });
      }
      const ov = (r?.overrides ?? {}) as JobOverrides;
      setO({
        engagement: ov.engagement ?? '',
        area: ov.area ?? '',
        workModel: ov.workModel ?? '',
        seniority: ov.seniority ?? '',
        city: ov.city ?? '',
        salaryMin: ov.salaryMin != null ? String(ov.salaryMin) : '',
        salaryMax: ov.salaryMax != null ? String(ov.salaryMax) : '',
        currency: ov.currency ?? '',
        salaryPeriod: ov.salaryPeriod ?? '',
        validThrough: ov.validThrough ?? '',
        blurbEn: ov.companyBlurb?.en ?? '',
        blurbBg: ov.companyBlurb?.bg ?? '',
      });
      setLoading(false);
    });
  }, [id]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (f.salary && !f.currency) {
      notify('danger', 'Currency is required when a salary is set.');
      return;
    }
    setSaving(true);
    try {
      const savedId = await saveRow('jobs', id, {
        title: f.title,
        status: f.status,
        content: f.content,
        workFromHome: f.workFromHome,
        salary: f.salary ? Number(f.salary) : null,
        currency: f.salary ? f.currency : null,
        // A Manatal sync never touches `overrides`; empty fields are removed so the site falls back to Manatal / heuristics.
        overrides: {
          engagement: o.engagement || deleteField(),
          area: o.area || deleteField(),
          workModel: o.workModel || deleteField(),
          seniority: o.seniority.trim() || deleteField(),
          city: o.city.trim() || deleteField(),
          salaryMin: o.salaryMin ? Number(o.salaryMin) : deleteField(),
          salaryMax: o.salaryMax ? Number(o.salaryMax) : deleteField(),
          currency: o.currency || deleteField(),
          salaryPeriod: o.salaryPeriod || deleteField(),
          validThrough: o.validThrough || deleteField(),
          companyBlurb:
            o.blurbEn.trim() || o.blurbBg.trim()
              ? { en: o.blurbEn.trim(), bg: o.blurbBg.trim() }
              : deleteField(),
        },
      });
      notify('success', 'Job saved.');
      navigate(`/jobs/${savedId}`);
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 720 }}>
      <h3 className="fw-bold mb-3">{id ? 'Edit job' : 'New job'}</h3>

      {Object.keys(synced).length > 0 && (
        <div className="alert alert-light border small">
          <strong>Synced from Manatal</strong> (read-only; use “Public site display” below to change
          what the site shows)
          <dl className="row mb-0 mt-2">
            {Object.entries(synced).map(([k, v]) => (
              <div key={k} className="col-sm-6 d-flex gap-2">
                <dt className="fw-normal text-muted">{k}</dt>
                <dd className="mb-1">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="mb-3">
        <label className="form-label fw-semibold">Title *</label>
        <input
          className="form-control"
          required
          maxLength={255}
          value={f.title}
          onChange={(e) => setF({ ...f, title: e.target.value })}
        />
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Status *</label>
        <select
          className="form-select"
          value={f.status}
          onChange={(e) => setF({ ...f, status: e.target.value })}
        >
          <option value="published">published</option>
          <option value="stopped">stopped</option>
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Description *</label>
        <QuillEditor value={f.content} onChange={(html) => setF({ ...f, content: html })} />
      </div>

      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="wfh"
          checked={f.workFromHome}
          onChange={(e) => setF({ ...f, workFromHome: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="wfh">
          Work from home
        </label>
      </div>

      <div className="row g-2 mb-3" style={{ maxWidth: 360 }}>
        <div className="col-7">
          <label className="form-label fw-semibold">Salary</label>
          <input
            type="number"
            min={0}
            className="form-control"
            value={f.salary}
            onChange={(e) => setF({ ...f, salary: e.target.value })}
          />
        </div>
        <div className="col-5">
          <label className="form-label fw-semibold">Currency</label>
          <select
            className="form-select"
            value={f.currency}
            onChange={(e) => setF({ ...f, currency: e.target.value })}
          >
            <option>BGN</option>
            <option>EUR</option>
            <option>USD</option>
          </select>
        </div>
      </div>

      <fieldset className="border rounded p-3 mb-4 bg-light">
        <legend className="fs-6 fw-bold px-2 w-auto">Public site display</legend>
        <p className="small text-muted">
          Optional. Leave a field on &quot;Automatic&quot; / empty to use Manatal&apos;s data or the
          site&apos;s default. These values are never overwritten by a Manatal import.
        </p>
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label fw-semibold">Engagement</label>
            <select className="form-select" value={o.engagement} onChange={(e) => setO({ ...o, engagement: e.target.value })}>
              <option value="">Automatic (from contract)</option>
              <option value="permanent">Permanent</option>
              <option value="b2b">B2B</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Area</label>
            <select className="form-select" value={o.area} onChange={(e) => setO({ ...o, area: e.target.value })}>
              <option value="">Automatic (from title)</option>
              {['backend', 'frontend', 'data', 'qa', 'leadership', 'other'].map((x) => (<option key={x} value={x}>{x}</option>))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Work model</label>
            <select className="form-select" value={o.workModel} onChange={(e) => setO({ ...o, workModel: e.target.value })}>
              <option value="">Automatic (remote flag)</option>
              <option value="remote">Remote</option>
              <option value="hybrid">Hybrid</option>
              <option value="onsite">On-site</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">City</label>
            <input className="form-control" value={o.city} onChange={(e) => setO({ ...o, city: e.target.value })} placeholder="Sofia" />
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Seniority</label>
            <input className="form-control" value={o.seniority} onChange={(e) => setO({ ...o, seniority: e.target.value })} placeholder="Senior" />
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Listing valid through</label>
            <input type="date" className="form-control" value={o.validThrough} onChange={(e) => setO({ ...o, validThrough: e.target.value })} />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-semibold">Salary from</label>
            <input type="number" min={0} className="form-control" value={o.salaryMin} onChange={(e) => setO({ ...o, salaryMin: e.target.value })} />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-semibold">Salary to</label>
            <input type="number" min={0} className="form-control" value={o.salaryMax} onChange={(e) => setO({ ...o, salaryMax: e.target.value })} />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-semibold">Currency</label>
            <select className="form-select" value={o.currency} onChange={(e) => setO({ ...o, currency: e.target.value })}>
              <option value="">Automatic</option>
              <option>BGN</option><option>EUR</option><option>USD</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label fw-semibold">Per</label>
            <select className="form-select" value={o.salaryPeriod} onChange={(e) => setO({ ...o, salaryPeriod: e.target.value })}>
              <option value="">Not stated</option>
              <option value="hour">hour</option><option value="day">day</option>
              <option value="month">month</option><option value="year">year</option>
            </select>
          </div>
          <div className="col-12 form-text mt-0">Without a period the range is shown but no &quot;per month&quot; is claimed and Google for Jobs gets no salary.</div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">About the hiring company (EN)</label>
            <textarea className="form-control" rows={3} value={o.blurbEn} onChange={(e) => setO({ ...o, blurbEn: e.target.value })} />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">About the hiring company (BG)</label>
            <textarea className="form-control" rows={3} value={o.blurbBg} onChange={(e) => setO({ ...o, blurbBg: e.target.value })} />
          </div>
        </div>
      </fieldset>

      <button className="btn btn-success" disabled={saving} type="submit">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}
